import { useCallback, useEffect, useMemo, useState } from "react";
import {
  authProviderAvailable,
  clearStoredSaleskoSession,
  exchangeBetterAuthSessionGrant,
  isStableBetterAuthSaleskoSession,
  readStoredSaleskoSession,
  SaleskoSessionChangeEvent,
  saleskoAuthProviders,
  startAuthProviderSignIn,
  writeStoredSaleskoSession
} from "../../lib/api";
import type { SaleskoOAuthProvider } from "@salesko/contracts";

export function useAgentSession() {
  const [storedSession, setStoredSession] = useState(() => readStoredSaleskoSession());

  useEffect(() => {
    const refreshSession = () => setStoredSession(readStoredSaleskoSession());
    window.addEventListener("focus", refreshSession);
    window.addEventListener("storage", refreshSession);
    window.addEventListener(SaleskoSessionChangeEvent, refreshSession);
    return () => {
      window.removeEventListener("focus", refreshSession);
      window.removeEventListener("storage", refreshSession);
      window.removeEventListener(SaleskoSessionChangeEvent, refreshSession);
    };
  }, []);

  useEffect(() => {
    let active = true;
    const current = readStoredSaleskoSession();
    if (!current || isStableBetterAuthSaleskoSession(current)) {
      return () => {
        active = false;
      };
    }

    async function refreshLegacySession() {
      try {
        const refreshed = await exchangeBetterAuthSessionGrant();
        if (!active) {
          return;
        }
        writeStoredSaleskoSession(refreshed);
        setStoredSession(refreshed);
      } catch {
        if (!active) {
          return;
        }
        clearStoredSaleskoSession();
        setStoredSession(null);
      }
    }

    void refreshLegacySession();
    return () => {
      active = false;
    };
  }, []);

  const agentAuthLabel = storedSession?.name ?? storedSession?.email ?? "Sign in";
  const availableAuthProviders = useMemo(() => saleskoAuthProviders.filter(authProviderAvailable), []);

  const handleAuthProviderSignIn = useCallback(async (provider: SaleskoOAuthProvider) => {
    await startAuthProviderSignIn(provider);
  }, []);

  return {
    agentAuthLabel,
    availableAuthProviders,
    handleAuthProviderSignIn,
    storedSession
  };
}
